import TR from 'utils/TR';
import T from 'utils/T';
import ex from 'utils/ex';

import WeekHeader from './WeekHeader';
import Week from './Week';
import YearMonth from './YearMonth';
import React from 'react';
import VBox from '../VBox';
import * as Luxon from 'luxon';

import './LinearCalendar.css';
import Day from './Day';
import HBox from '../HBox';
import PeriodButton from 'components/header/period/PeriodButton';
import WheelScroll from '../WheelScroll';
import { luxonClone } from 'utils/time';
import useDetectEffect from 'hooks/common/useDetectEffect';

const clip = (min, max, current) => {
  if (!min && max) {
    return Luxon.DateTime.min(max, current);
  } else if (min && !max) {
    return Luxon.DateTime.max(min, current);
  } else if (min && max) {
    return Luxon.DateTime.max(min, Luxon.DateTime.min(max, current));
  } else {
    return current;
  }
}

const onDefaultDisable = ({ today, luxon, selection, days }) => Luxon.DateTime.fromFormat(today, 'yyyyMMdd') < luxon || (selection && (days < 13 || 89 < days));

export default function LinearCalendar(props) {
  const {
    date_availables,
    selection_start,
    selection_end,
    selection,
    onSelect,
    min,
    max,
    basis: lastBasis,
    forceBasis,
    length = 12,
    onPeriodChanged,
    onDisable = onDefaultDisable,
    ...rest
  } = props || {};

  const today = React.useMemo(() => Luxon.DateTime.now().toFormat('yyyyMMdd'), []);
  const weeksInWeekYear = React.useMemo(() => Luxon.DateTime.now().endOf('year').weeksInWeekYear, []);

  const [basis, setBasis] = React.useState(() => luxonClone(lastBasis));
  React.useEffect(() => {
    if (!forceBasis) return;
    setBasis(() => forceBasis);
  }, [forceBasis]);

  const calendar = React.useMemo(() => {
    let basisWeekend = basis.endOf('week', { useLocaleWeeks: true });
    let lastIndex = 0;
    const list = [];
    if (basis.month !== basisWeekend.month) {
      lastIndex = basisWeekend.day;
      basisWeekend = basis.endOf('month');
    }
    while (list.length < length) {
      const week = [];
      Array.from({ length: lastIndex }).forEach(() => {
        week.push({ empty: true });
      });
      let lastYear;
      let lastMonth;
      let lastDay;
      // eslint-disable-next-line no-loop-func
      for (let index = lastIndex; index < 7; ++index) {
        const day = basisWeekend.toFormat('d');
        const month = basisWeekend.toFormat('M');
        const year = basisWeekend.toFormat('yyyy');
        const date = basisWeekend.toFormat('yyyyMMdd');
        lastYear = year;
        lastMonth = month;
        lastDay = day;
        week.push({ day, date });
        basisWeekend = basisWeekend.minus({ days: 1 });
        if (+day === 1) {
          Array.from({ length: 7 - index - 1 }).forEach(() => {
            week.push({ empty: true });
          });
          lastIndex = (index + 1) % 7;
          break;
        } else {
          lastIndex = 0;
        }
      }
      list.push(week.reverse());
      if (+lastDay === 1) {
        list.push({ year: lastYear, month: lastMonth });
      }
    }
    return list.reverse();
  }, [basis, length]);

  const onWheel = React.useCallback((e) => {
    let delta = { days: 7 };
    if (e.shiftKey) {
      delta = { years: 1 };
    }
    if (e.altKey) {
      delta = { months: 1 };
    }
    if (0 < e.deltaY) {
      setBasis(lastBasis => {
        const next = lastBasis.plus(delta);
        if (next.month === lastBasis.month) {
          return luxonClone(clip(min, max, next));
        } else {
          return luxonClone(clip(min, max, next.startOf('month')));
        }
      });
    } else {
      setBasis(lastBasis => {
        const prev = lastBasis.minus(delta);
        if (prev.month === lastBasis.month) {
          return luxonClone(clip(min, max, prev))
        } else {
          return luxonClone(clip(min, max, prev.endOf('month')));
        }
      });
    }
  }, [max, min]);

  const onWheelUpward = React.useCallback((e) => {
    let delta = { days: 7 };
    if (e.shiftKey) {
      delta = { years: 1 };
    }
    if (e.altKey) {
      delta = { months: 1 };
    }
    setBasis(lastBasis => {
      const next = lastBasis.plus(delta);
      if (next.month === lastBasis.month) {
        return luxonClone(clip(min, max, next));
      } else {
        return luxonClone(clip(min, max, next.startOf('month')));
      }
    });
  }, [max, min]);

  const onWheelDownward = React.useCallback((e) => {
    let delta = { days: 7 };
    if (e.shiftKey) {
      delta = { years: 1 };
    }
    if (e.altKey) {
      delta = { months: 1 };
    }

    setBasis(lastBasis => {
      const prev = lastBasis.minus(delta);
      if (prev.month === lastBasis.month) {
        return luxonClone(clip(min, max, prev))
      } else {
        return luxonClone(clip(min, max, prev.endOf('month')));
      }
    });
  }, [max, min]);

  const onMonthClick = React.useCallback((year, month) => (e) => {
    const first = Luxon.DateTime.local(+year, +month, 1, 0, 0, 0, 0);
    const last = first.endOf('month');
    onSelect(first, last)(e);
  }, [onSelect]);

  React.useEffect(() => {
    const c = calendar.flat();
    let i = 0;
    let s = c[0] || [];
    while ((s.empty || !s.date) && i < c.length) {
      ++i;
      s = c[i];
    }

    i = c.length - 1;
    let e = c[i];
    while ((e.empty || !e.date) && i !== 0) {
      --i;
      e = c[i];
    }
    onPeriodChanged && onPeriodChanged(s.date, e.date);
  }, [calendar, onPeriodChanged]);

  return (
    <WheelScroll
      {...ex('LinearCalendar', rest)}
      {...{
        onWheelUpward,
        onWheelDownward,
        total: weeksInWeekYear,
        current: luxonClone(basis).weekNumber - 3,
        size: 3,
      }}
    >
      <WeekHeader />
      <div {...ex('Contents')}>
        <HBox>
          {calendar.map((content, contentIndex) => {
            if (content instanceof Array) {
              return content.map(({ day, date, luxon, empty }, weekdayIndex) => {
                if (empty) {
                  return (
                    <Day key={`${contentIndex}-${weekdayIndex}`} />
                  );
                } else {
                  const luxon = Luxon.DateTime.fromFormat(date, 'yyyyMMdd');
                  const days = selection && Math.abs(luxon.diff(selection, ['days']).days);

                  // 14/90이 아니고 13/89인 이유는 luxon에서는 오늘을 0으로 보지만, 앱에서는 1로 보기 때문
                  // const disabled = Luxon.DateTime.fromFormat(today, 'yyyyMMdd') < luxon || (selection && (days < 13 || 89 < days));
                  const disabled = onDisable({ today, luxon, selection, days });
                  const selected = [
                    ...(selection ? [selection.toFormat('yyyyMMdd')] : []),
                    ...(selection_start ? [selection_start.toFormat('yyyyMMdd')] : []),
                    ...(selection_end ? [selection_end.toFormat('yyyyMMdd')] : []),
                  ].includes(date);
                  const between = selection_start && selection_end && selection_start <= luxon && luxon <= selection_end;
                  const forward = luxon.day === 1 || luxon.weekday === 7 || (selection_start && selection_start.toFormat('yyyyMMdd') === date);
                  const backward = luxon.day === luxon.endOf('month').day || luxon.weekday === 6 || (selection_end && selection_end.toFormat('yyyyMMdd') === date);
                  return (
                    <Day
                      key={date}
                      {...{
                        luxon,
                        today: date === today,
                        disabled,
                        selected,
                        between,
                        forward,
                        backward,
                        available: (min ? min <= luxon : true) && (max ? luxon <= max : true) && date_availables[date],
                        onClick: onSelect(luxon),
                      }}
                    />
                  )
                }
              });
            } else {
              const {
                year,
                month,
              } = content;
              return year && month && (
                <YearMonth key={`${year}/${month}`} {...{ year, month, onClick: onMonthClick(year, month) }} />
              );
            }
          }).flat()}
        </HBox>
      </div>
    </WheelScroll>
  );
}
